<template>
  <tx-dialog
    v-model="visible"
    :title="t('personalSettings.copySavedViews.title')"
    height="580px"
    width="500px"
    class="custom-dialog"
  >
    <div class="p-6 pt-4 border-t border-gray-200">
      <p class="mb-4 text-gray-700 text-sm">
        {{ t('personalSettings.copySavedViews.description') }}
      </p>

      <div class="overflow-y-auto h-96">
        <tx-tree
          v-if="savedViewTree.length"
          ref="refTree"
          class="tree"
          show-checkbox="onHoverOrOtherChecked"
          :data="savedViewTree"
          @check-change="onTreeCheckChange"
        />
        <div v-else>
          <p>{{ t('personalSettings.copySavedViews.emptyTree') }}</p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="border-t border-gray-200 flex justify-center items-center py-4 space-x-4">
        <tx-button type="cancel" :text="t('general.close')" width="160px" @click="onCancel" />
        <tx-button type="confirm" :text="t('general.copy')" width="160px" @click="onCopy" />
      </div>
    </template>
  </tx-dialog>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TxDialog from '@/shared/components/TxDialog.vue'
import TxButton from '@/shared/components/TxButton.vue'
import TxTree from '@/shared/components/TxTree.vue'
import { useUserStore } from '@/store/userData'
import { createSavedView, getOtherSavedViews } from '@/api/t1/savedView'
import utils from '@/services/utils'
import { useNotificationStore } from '@/store/notification'
import appConfig from '@/services/appConfig'

const { t } = useI18n()
const userStore = useUserStore()
const notificationsStore = useNotificationStore()

const visible = ref(false)
const refTree = ref<InstanceType<typeof TxTree>>()
const savedViewTree = ref<ITreeNode[]>([])
const selectedSavedViewTreeNodes = ref<ITreeNode[]>([])
const myCurrentSavedViews = ref<any[]>([])

async function getAvailableSavedViews() {
  const otherSavedViews = await getOtherSavedViews(userStore.activeCatalog!.CatalogCode)
  const indexedOtherCatalogsUserSavedViews: Record<string, any> = {}

  otherSavedViews.data.forEach((savedView) => {
    if (!savedView.SeasonFilterApplied && savedView.CatalogCode) {
      if (!indexedOtherCatalogsUserSavedViews[savedView.CatalogCode]) {
        indexedOtherCatalogsUserSavedViews[savedView.CatalogCode] = []
      }
      indexedOtherCatalogsUserSavedViews[savedView.CatalogCode].push({
        key: `${savedView.CatalogCode}_${savedView.Id}`,
        label: savedView.Name,
        path: [],
        children: [],
        checked: false,
        expanded: false,
        sortOrder: 0,
        actions: [],
        saveViewValue: savedView,
      })
    }
  })

  const tree: ITreeNode[] = Object.entries(indexedOtherCatalogsUserSavedViews).map(([catalogCode, views]) => (
    {
      key: catalogCode,
      label: views[0].saveViewValue.CatalogName,
      path: [],
      children: utils.sort(views, 'label'),
      checked: false,
      expanded: false,
      sortOrder: 0,
      actions: [],
    }
  ))

  savedViewTree.value = utils.sort(tree, 'label')
}

function onTreeCheckChange() {
  const checkedNodes = refTree.value?.getCheckedNodes(true)
  selectedSavedViewTreeNodes.value = []
  if (utils.isDefined(checkedNodes) && checkedNodes.length > 0) {
    selectedSavedViewTreeNodes.value = checkedNodes
  }
}

async function onCopy() {
  if (!userStore.activeCatalog || selectedSavedViewTreeNodes.value.length === 0) {
    return
  }
  try {
    const currentUserSavedViewsLower = myCurrentSavedViews.value.map(view => view.Name.toString().trim().toLowerCase())
    const createPromises = selectedSavedViewTreeNodes.value.map(async (node) => {
      const savedView = node.saveViewValue
      if (savedView) {
        const currentSettingNameLower = savedView.Name.toString().trim().toLowerCase()
        const postfix = utils.getPostfix(currentSettingNameLower, currentUserSavedViewsLower)
        const newName = savedView.Name.toString().trim() + postfix
        const payload = {
          ...savedView,
          Name: newName,
          CatalogCode: userStore.activeCatalog!.CatalogCode,
          IsCatalogLevel: false,
          CreatedBy: userStore.userProfile?.Id,
          ModifiedBy: userStore.userProfile?.Id,
        }
        currentUserSavedViewsLower.push(newName.toLowerCase())
        return createSavedView(userStore.activeCatalog!.CatalogCode, payload)
      }
    })
    await Promise.all(createPromises)
    await userStore.doLoadData(['SavedViews'])
    notificationsStore.addNotification({ message: t('personalSettings.copySavedViews.copiedSuccessfully'), type: 'Success' })
    onCancel()
  }
  catch (e) {
    console.error(e)
    notificationsStore.addNotification({ message: t('general.unexpectedError'), type: 'Warning' })
    onCancel()
  }
}

async function getMySavedViews() {
  if (userStore.activeCatalog && userStore.userProfile) {
    const personalViews = await appConfig.DB!.savedViews.where({ CatalogCode: userStore.activeCatalog!.CatalogCode, CreatedBy: userStore.userProfile.Id, IsCatalogLevel: 0, Status: 1 }).toArray()
    const catalogViews = await appConfig.DB!.savedViews.where({ CatalogCode: userStore.activeCatalog!.CatalogCode, IsCatalogLevel: 1, Status: 1 }).toArray()
    myCurrentSavedViews.value = [...personalViews, ...catalogViews]
  }
}

function onCancel() {
  visible.value = false
}

function show() {
  visible.value = true
  getAvailableSavedViews()
  getMySavedViews()
}

defineExpose({ show })
</script>
